// ============================================================
// SIDEBAR COMPONENT
// ============================================================

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, UtensilsCrossed, FileCheck, ShieldCheck,
  UserCheck, BarChart3, TrendingUp, ShoppingBag, DollarSign, Percent,
  Headphones, Lock, Settings2, Building2, XCircle, Ban,
  Wallet, TestTube2, Settings, UserCircle, ChevronLeft, ChevronRight,
  LogOut, Clock, Timer, PackageCheck,
} from 'lucide-react';
import { NAV_ITEMS, NAV_GROUPS, getAllowedNavIds } from '../../utils/constants';
import { useAuth } from '../../contexts/AuthContext';

const ICON_MAP = {
  LayoutDashboard, Users, UtensilsCrossed, FileCheck, ShieldCheck,
  UserCheck, BarChart3, TrendingUp, ShoppingBag, DollarSign, Percent,
  Headphones, HeadphonesIcon: Headphones, Lock, Settings2, Building2,
  XCircle, Ban, Wallet, TestTube2, Settings, UserCircle,
  Clock, Timer, PackageCheck,
};

const Sidebar = ({ collapsed, onToggle, mobileOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Filter nav items by the logged-in user's admin_role
  // Falls back to `role` field for backwards compatibility with existing sessions
  const allowedIds = getAllowedNavIds(user?.admin_role || user?.role);
  const visibleItems = allowedIds === null ? NAV_ITEMS : NAV_ITEMS.filter(item => allowedIds.includes(item.id));

  // Group nav items
  const groupedItems = NAV_GROUPS.map(group => ({
    group,
    items: visibleItems.filter(item => item.group === group),
  })).filter(g => g.items.length > 0);

  return (
    <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      {/* Logo */}
      <div className="admin-sidebar-logo">
        <div className="admin-sidebar-logo-icon">🍛</div>
        <div className="admin-sidebar-logo-text">
          <h2>Biryani</h2>
          <span>Admin Panel</span>
        </div>
      </div>

      {/* User Info */}
      {!collapsed && (
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, #E85D04, #F48C06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 700, fontSize: 13, flexShrink: 0,
          }}>
            {user?.name?.charAt(0) || 'A'}
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ color: '#F1F5F9', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.name || 'Admin'}
            </div>
            <div style={{ color: '#E85D04', fontSize: 11 }}>{user?.role}</div>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="admin-sidebar-nav">
        {groupedItems.map(({ group, items }) => (
          <div key={group}>
            {!collapsed && (
              <div style={{
                padding: '12px 20px 4px',
                fontSize: 10, fontWeight: 700, letterSpacing: 1,
                color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase',
              }}>{group}</div>
            )}
            {items.map(item => {
              const Icon = ICON_MAP[item.icon];
              const isActive = location.pathname === item.path;
              return (
                <div
                  key={item.id}
                  className={`admin-nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => navigate(item.path)}
                  title={collapsed ? item.label : ''}
                >
                  {Icon && <Icon size={18} className="admin-nav-icon" />}
                  <span className="admin-nav-label">{item.label}</span>
                </div>
              );
            })}
          </div>
        ))}
        {/* Logout */}
        <div
          className="admin-nav-item"
          onClick={handleLogout}
          title={collapsed ? 'Logout' : ''}
          style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16 }}
        >
          <LogOut size={18} className="admin-nav-icon" />
          <span className="admin-nav-label">Logout</span>
        </div>
      </nav>

      {/* Toggle */}
      <div className="admin-sidebar-footer">
        <button className="admin-sidebar-toggle" onClick={onToggle}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
